import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, textareaClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (status === 422 && data.detail) {
    if (Array.isArray(data.detail)) {
      const first = data.detail[0];
      if (typeof first === 'object') return `${first.loc?.[first.loc.length - 1] || 'Field'}: ${first.msg || 'Invalid value'}`;
      return String(first);
    }
    if (typeof data.detail === 'string') return data.detail;
  }
  if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  if (data.message) return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
  return `Request failed with status ${status}`;
};

const CustomerForm = ({ customer, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    customer_type: 'business',
    name: '',
    email: '',
    phone: '',
    ntn: '',
    address: '',
    city: '',
    credit_limit: 0,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (customer) {
      setFormData({
        customer_type: customer.customer_type || 'business',
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        ntn: customer.ntn || '',
        address: customer.address || '',
        city: customer.city || '',
        credit_limit: customer.credit_limit || 0,
        is_active: customer.is_active !== undefined ? customer.is_active : true
      });
    }
  }, [customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name || formData.name.trim() === '') {
      setError('Customer name is required');
      return;
    }
    setLoading(true);
    try {
      if (customer) {
        await api.put(`/customers/${customer.id}`, formData);
      } else {
        await api.post('/customers', formData);
      }
      onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
      console.error('Customer save error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title={customer ? 'Edit Customer' : 'Add New Customer'}
      onClose={onClose}
      maxWidth="max-w-2xl"
      dataTestId="customer-form-modal"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors"
            data-testid="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="customer-form"
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            data-testid="save-customer-button"
          >
            {loading ? 'Saving...' : (customer ? 'Update Customer' : 'Add Customer')}
          </button>
        </div>
      }
    >
      <form id="customer-form" onSubmit={handleSubmit}>
        {error && (
          <div className={errorBoxClass} data-testid="form-error">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Customer Type</label>
            <select
              className={selectClass}
              value={formData.customer_type}
              onChange={(e) => setFormData({ ...formData, customer_type: e.target.value })}
              data-testid="customer-type-select"
            >
              <option value="business">Business</option>
              <option value="individual">Individual</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Name {requiredMark}</label>
            <input
              type="text"
              className={inputClass}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="customer-name-input"
            />
          </div>

          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              className={inputClass}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="customer-email-input"
            />
          </div>

          <div>
            <label className={labelClass}>Phone</label>
            <input
              type="tel"
              className={inputClass}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              data-testid="customer-phone-input"
            />
          </div>

          <div>
            <label className={labelClass}>NTN</label>
            <input
              type="text"
              className={inputClass}
              value={formData.ntn}
              onChange={(e) => setFormData({ ...formData, ntn: e.target.value })}
              data-testid="customer-ntn-input"
            />
          </div>

          <div>
            <label className={labelClass}>City</label>
            <input
              type="text"
              className={inputClass}
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              data-testid="customer-city-input"
            />
          </div>

          <div>
            <label className={labelClass}>Credit Limit (PKR)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={inputClass}
              value={formData.credit_limit}
              onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) })}
              data-testid="customer-credit-input"
            />
          </div>

          {customer && (
            <div className="flex items-center pt-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="mr-2 w-4 h-4 accent-[#4A7AE0]"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  data-testid="customer-active-checkbox"
                />
                <span className="text-sm text-[#1a1a2e]">Active</span>
              </label>
            </div>
          )}
        </div>

        <div className="mt-4">
          <label className={labelClass}>Address</label>
          <textarea
            className={textareaClass}
            rows="2"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            data-testid="customer-address-input"
          />
        </div>
      </form>
    </ModalShell>
  );
};

export default CustomerForm;
